import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Badge,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Alert,
  AlertIcon,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  Select
} from '@chakra-ui/react';
import { 
  ChevronRightIcon, 
  AddIcon,
  EditIcon,
  DeleteIcon,
  CopyIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  FiGrid, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiCopy, 
  FiMoreVertical,
  FiSearch,
  FiLayout,
  FiFileText,
  FiNavigation,
  FiImage,
  FiDatabase,
  FiShoppingCart
} from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';

const ComponentLibrary = () => {
  const [components, setComponents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  useEffect(() => {
    if (tenantId) {
      fetchComponents();
      fetchCategories();
    }
  }, [tenantId]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/page-components/${tenantId}`);
      let componentsData = [];
      if (Array.isArray(response)) {
        componentsData = response.flat();
      } else if (response.data) {
        componentsData = Array.isArray(response.data) ? response.data.flat() : [];
      }
      setComponents(componentsData);
    } catch (error) {
      console.error('Error fetching components:', error);
      toast({
        title: 'Error loading components',
        description: error.message || 'Failed to load components',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient(`/component-categories/${tenantId}`);
      let categoriesData = [];
      if (Array.isArray(response)) {
        categoriesData = response.flat();
      } else if (response.data) {
        categoriesData = Array.isArray(response.data) ? response.data.flat() : [];
      }
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleDeleteComponent = async (component) => {
    if (!window.confirm(`Are you sure you want to delete "${component.name}"?`)) {
      return;
    }

    try {
      const response = await apiClient(`/page-components/${tenantId}/${component.id}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Component deleted successfully',
        description: `"${component.name}" has been deleted`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchComponents();
    } catch (error) {
      console.error('Error deleting component:', error);
      toast({
        title: 'Error deleting component',
        description: error.message || 'Failed to delete component',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDuplicateComponent = async (component) => {
    try {
      const duplicatedComponent = {
        ...component,
        name: `${component.name} (Copy)`,
        identifier: `${component.identifier}-copy-${Date.now()}`,
        is_system: false
      };

      delete duplicatedComponent.id;
      delete duplicatedComponent.created_at;
      delete duplicatedComponent.updated_at;

      const response = await apiClient(`/page-components/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(duplicatedComponent)
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Component duplicated successfully',
        description: `"${component.name}" has been copied`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchComponents();
    } catch (error) {
      console.error('Error duplicating component:', error);
      toast({
        title: 'Error duplicating component',
        description: error.message || 'Failed to duplicate component',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'layout': return FiLayout;
      case 'content': return FiFileText;
      case 'navigation': return FiNavigation;
      case 'media': return FiImage;
      case 'dynamic': return FiDatabase;
      case 'ecommerce': return FiShoppingCart;
      default: return FiGrid;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'layout': return 'blue';
      case 'content': return 'green';
      case 'navigation': return 'purple';
      case 'media': return 'pink';
      case 'dynamic': return 'teal';
      case 'ecommerce': return 'yellow';
      default: return 'gray';
    }
  };

  // Filter components based on search and category (now with select)
  const filteredComponents = components.filter(component => {
    const matchesSearch = !searchTerm || 
      component.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      component.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || component.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group components by category
  const componentsByCategory = categories.reduce((acc, category) => {
    acc[category.identifier] = filteredComponents.filter(
      comp => comp.category === category.identifier
    );
    return acc;
  }, {});

  // console.log('All components:', components);
  // console.log('Filtered components:', filteredComponents);
  // console.log('Categories:', categories);
  // console.log('Components by category:', componentsByCategory);

  if (!tenantId) {
    return (
      <Container maxW="container.2xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.2xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/pages">Pages</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Component Library</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Component Library</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => navigate('/component-library/create')}
          >
            Create Component
          </Button>
        </HStack>

        <HStack spacing={4}>
          <InputGroup maxW="400px">
            <InputLeftElement>
              <FiSearch />
            </InputLeftElement>
            <Input
              placeholder="Search components..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Select
            maxW="250px"
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((category, idx) => (
              <option key={idx} value={category}>{category}</option>
            ))}
          </Select>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
          {filteredComponents.map(component => (
            <ComponentCard
              key={component.id}
              component={component}
              onEdit={() => navigate(`/component-library/edit/${component.id}`)}
              onDelete={() => handleDeleteComponent(component)}
              onDuplicate={() => handleDuplicateComponent(component)}
              getCategoryIcon={getCategoryIcon}
              getCategoryColor={getCategoryColor}
            />
          ))}
        </SimpleGrid>

        {filteredComponents.length === 0 && !loading && (
          <Box textAlign="center" py={12}>
            <FiGrid size={48} color="gray.400" />
            <Heading size="md" mt={4} mb={2} color="gray.500">
              No components found
            </Heading>
            <Text color="gray.500" mb={6}>
              {searchTerm || selectedCategory !== 'all' 
                ? "Try adjusting your search or filter criteria"
                : "Get started by creating your first component"
              }
            </Text>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => navigate('/component-library/create')}
            >
              Create Component
            </Button>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

const ComponentCard = ({ 
  component, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  getCategoryIcon, 
  getCategoryColor 
}) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.600');

  const IconComponent = getCategoryIcon(component.category);

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="1px"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      transition="all 0.2s"
    >
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Box
              p={2}
              borderRadius="md"
              bg={`${getCategoryColor(component.category)}.100`}
              color={`${getCategoryColor(component.category)}.600`}
            >
              <IconComponent size={16} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                {component.name}
              </Text>
              <Badge 
                size="xs" 
                colorScheme={getCategoryColor(component.category)}
                variant="subtle"
              >
                {component.category}
              </Badge>
            </VStack>
          </HStack>
          
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FiMoreVertical />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem icon={<FiEdit />} onClick={onEdit}>
                Edit
              </MenuItem>
              <MenuItem icon={<FiCopy />} onClick={onDuplicate}>
                Duplicate
              </MenuItem>
              {!component.is_system && (
                <MenuItem icon={<FiTrash2 />} onClick={onDelete} color="red.500">
                  Delete
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
      </CardHeader>

      <CardBody pt={0}>
        {component.preview_image && (
          <Image
            src={component.preview_image}
            alt={component.name}
            borderRadius="md"
            mb={3}
            h="120px"
            w="100%"
            objectFit="cover"
            bg="gray.100"
          />
        )}
        
        <Text fontSize="xs" color="gray.600" noOfLines={3} mb={3}>
          {component.description}
        </Text>

        <HStack justify="space-between" align="center">
          <HStack spacing={1}>
            {component.is_system && (
              <Badge size="xs" colorScheme="purple">
                System
              </Badge>
            )}
            <Badge size="xs" variant="outline">
              v{component.version || '1.0'}
            </Badge>
          </HStack>
          
          <Text fontSize="xs" color="gray.500">
            {component.fields ? JSON.parse(component.fields).length : 0} fields
          </Text>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default ComponentLibrary;